import * as React from 'react';
import {type ComponentProps} from 'react';
import {useTranslation} from 'react-i18next';
import {Search, X} from 'lucide-react';
import {cn} from '../../lib/cn';
import {Button} from '../../button';
import {AirbnbFieldHelperText} from '../field-helper-text';

type AirbnbSearchInputProps = ComponentProps<'input'> & {
  onReset?: () => void;
  helper?: React.ReactNode;
  wrapperClassName?: string;
};

export const AirbnbSearchInput = React.forwardRef<
  HTMLInputElement,
  AirbnbSearchInputProps
>(({onReset, placeholder, helper, wrapperClassName, ...props}, ref) => {
  const {t} = useTranslation();
  const generatedId = React.useId();
  const helperId = helper ? `${props.id ?? generatedId}-helper` : undefined;

  const placeholderText = placeholder || t('search_property') + '...';

  const field = (
    <div className={cn('input-wrapper relative', wrapperClassName)}>
      <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 transform text-[var(--chekin-neutral-500)]" />
      <input
        {...props}
        ref={ref}
        type="text"
        disabled={props.disabled}
        value={props.value}
        defaultValue={props.defaultValue}
        onChange={props.onChange}
        onFocus={props.onFocus}
        onBlur={props.onBlur}
        placeholder={placeholderText}
        aria-describedby={helperId ?? props['aria-describedby']}
        className={cn(
          'placeholder-shown:bg-accent flex h-10 w-full rounded-md border border-input bg-background py-3 pl-12 pr-4 text-base text-[#2d3748] ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-base placeholder:font-medium placeholder:text-[var(--chekin-neutral-500)] focus:border-gray-300 focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
          props.className,
        )}
      />
      {onReset && (
        <Button
          variant="ghost"
          onClick={onReset}
          className="absolute right-0 top-1/2 h-5 w-5 -translate-y-1/2 transform text-[var(--chekin-neutral-500)]"
        >
          <X className="h-5 w-5" />
        </Button>
      )}
    </div>
  );

  if (!helper) {
    return field;
  }

  return (
    <div className="w-full">
      {field}
      <AirbnbFieldHelperText id={helperId} helper={helper} disabled={props.disabled} />
    </div>
  );
});

AirbnbSearchInput.displayName = 'AirbnbSearchInput';
